import { ThemeSchemaResponse } from '@api';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { queryKeys } from '@terminal/routes/query-params';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { AssetsOverviewTab } from '@terminal/widgets/company-to-theme-widget/tabs/assets-overview-tab/AssetsOverviewTab';
import { useTranslation } from 'react-i18next';
import styles from './CompanyToThemeWidget.module.scss';
import { CompanyExposureTab } from './tabs/company-exposure-tab/CompanyExposureTab';
import { CompanyFinancialMacroExposureTab } from './tabs/company-financial-macro-exposure-tab/CompanyFinancialMacroExposureTab';

enum CompanyToThemeSubTabsType {
  CompanyExposure = 'companyExposure',
  AssetOverview = 'assetOverview',
}

interface CompanyToThemeWidgetProps {
  theme: ThemeSchemaResponse;
}

export const CompanyToThemeWidget = ({ theme }: CompanyToThemeWidgetProps) => {
  const { t } = useTranslation(['widget']);
  const { params, setQueryParams, deleteQueryParam } = useQueryParams();
  const isMacroFinancialThemeEnabled = useFlags(state =>
    state.getFeatureFlagState('KG-786-Macro_Financial_Theme')
  );
  const entityTag = params[queryKeys.entityTag];

  const {
    isLoading: isEntityLoading,
    isError: isEntityError,
    error: entityError,
    data: entityData,
  } = useEntity(entityTag ?? '');

  const subTabs = [
    {
      label: t('widget:themeOverview.drawer.companyExposure'),
      key: CompanyToThemeSubTabsType.CompanyExposure,
    },
    {
      label: t('widget:themeOverview.drawer.assetOverview'),
      key: CompanyToThemeSubTabsType.AssetOverview,
    },
  ];

  const foundIndex = subTabs.findIndex(
    t => t.key === params[queryKeys.activeDrawerTab]
  );
  const activeIndex = foundIndex !== -1 ? foundIndex : 0;

  const onTabChange = (tabIndex: number) => {
    const subTab = Object.values(CompanyToThemeSubTabsType)[tabIndex];

    if (subTab) {
      setQueryParams({ [queryKeys.activeDrawerTab]: subTab });
    } else {
      deleteQueryParam(queryKeys.activeDrawerTab);
    }
  };

  const renderContent = () => {
    if (isEntityLoading) {
      return (
        <div
          className={styles.loaderWrapper}
          data-testid="company-to-theme-loader"
        >
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '100%',
                  height: '40px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '65%',
                  height: '32px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '55%',
                  height: '32px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
        </div>
      );
    }

    if (isEntityError || !entityData?.data) {
      throw new Error(
        entityError?.message || 'CompanyToThemeWidget: no entity data found'
      );
    }

    const isFinancialOrMacro =
      theme.type === 'financial' || theme.type === 'macro';
    switch (params[queryKeys.activeDrawerTab]) {
      case CompanyToThemeSubTabsType.AssetOverview:
        return <AssetsOverviewTab entity={entityData.data} theme={theme} />;
      default:
        return isFinancialOrMacro && isMacroFinancialThemeEnabled ? (
          <CompanyFinancialMacroExposureTab
            entity={entityData.data}
            theme={theme}
          />
        ) : (
          <CompanyExposureTab entity={entityData.data} theme={theme} />
        );
    }
  };

  if (!entityTag) {
    return null;
  }

  return (
    <div className={styles.container}>
      <ButtonGroup
        className={styles.styledButtonGroup}
        labels={subTabs.map(t => t.label)}
        activeIndex={activeIndex}
        onClick={onTabChange}
      />
      <ErrorBoundaryWrapper resetKeys={[activeIndex]}>
        {renderContent()}
      </ErrorBoundaryWrapper>
    </div>
  );
};
